import { useEffect, useMemo, useState } from "react";
import {
  DataGridPremium,
  GridColDef,
  GridColumnVisibilityModel,
  GridInitialState,
  GridRenderCellParams,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import { Tooltip } from "@mui/material";
import Search from "assets/icons/search.gif";
import piDeliverancesService from "api/piDeliverancesService";
import { IPIDeliverance } from "dto/PI/IPIDeliverance";
import InfoIcon from "assets/icons/info_icon.svg?react";
import PIDeliveranceMetricSlide from "./PIDeliveranceMetricSlide";
import highlightText from "utils/highlightText";
import { plGridLocales } from "utils/muiGridLocales";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import useGridRowSelection from "hooks/useGridRowSelection";

const GRID_KEY = "grid_pi_deliverances_v1";

const fmtDate = (v?: string): string => (v ? moment(v).format("DD.MM.YYYY") : "");

const htmlCell = (value: string, searchText?: string) => (
  <div
    className={styles.cell}
    title={value}
    dangerouslySetInnerHTML={{
      __html: highlightText(value ?? "", searchText ?? ""),
    }}
  />
);

const CustomNoRowsOverlay = () => (
  <div className={styles.empty}>
    <img src={Search} alt="" />
    <div>Brak doręczeń w rejestrze</div>
  </div>
);

const PIDeliverancesRegister: FC<Props> = ({ searchText, setSubtitle }) => {
  const apiRef = useGridApiRef();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();

  const storedState = (): GridInitialState => {
    try {
      const raw = localStorage.getItem(GRID_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const [initialState] = useState<GridInitialState>(storedState);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(
      () => storedState()?.columns?.columnVisibilityModel ?? {},
    );

  const [metricOf, setMetricOf] = useState<IPIDeliverance | null>(null);

  const saveSnapshotInternal = () => {
    try {
      localStorage.setItem(
        GRID_KEY,
        JSON.stringify(apiRef.current.exportState()),
      );
    } catch {
      /* zapis ustawień siatki jest opcjonalny */
    }
  };

  const { data, isLoading } =
    piDeliverancesService.useGetPIDeliverances(searchText);

  const rows = useMemo(
    () => (Array.isArray(data) ? data : []).map((d) => ({ id: d.Id, ...d })),
    [data],
  );
  rowsRef.current = rows;

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "CaseSignature",
        headerName: "Sygnatura",
        minWidth: 140,
        renderHeader: () => <div className={styles.header}>Sygnatura</div>,
        renderCell: (p: GridRenderCellParams) => htmlCell(p.value, searchText),
      },
      {
        field: "Court",
        headerName: "Sąd",
        flex: 1,
        minWidth: 180,
        renderHeader: () => <div className={styles.header}>Sąd</div>,
        renderCell: (p: GridRenderCellParams) => htmlCell(p.value, searchText),
      },
      {
        field: "Department",
        headerName: "Wydział",
        width: 180,
        renderHeader: () => <div className={styles.header}>Wydział</div>,
        renderCell: (p: GridRenderCellParams) => htmlCell(p.value, searchText),
      },
      {
        field: "DocumentType",
        headerName: "Rodzaj",
        width: 140,
        renderHeader: () => <div className={styles.header}>Rodzaj</div>,
        renderCell: (p: GridRenderCellParams) => htmlCell(p.value, searchText),
      },
      {
        field: "Title",
        headerName: "Pismo",
        flex: 1,
        minWidth: 200,
        renderHeader: () => <div className={styles.header}>Pismo</div>,
        renderCell: (p: GridRenderCellParams) => htmlCell(p.value, searchText),
      },
      {
        field: "PublicationDate",
        headerName: "Publikacja",
        width: 120,
        renderHeader: () => <div className={styles.header}>Publikacja</div>,
        valueFormatter: (v) => fmtDate(v),
      },
      {
        field: "DeliveryDate",
        headerName: "Doręczenie",
        width: 120,
        renderHeader: () => <div className={styles.header}>Doręczenie</div>,
        valueFormatter: (v) => fmtDate(v),
      },
      {
        field: "ReadDate",
        headerName: "Odczytano",
        width: 120,
        renderHeader: () => <div className={styles.header}>Odczytano</div>,
        valueFormatter: (v) => fmtDate(v),
      },
      {
        field: "Status",
        headerName: "Status",
        width: 130,
        renderHeader: () => <div className={styles.header}>Status</div>,
        renderCell: (p: GridRenderCellParams) => htmlCell(p.value, searchText),
      },
      {
        field: "metric",
        headerName: "Met.",
        width: 70,
        sortable: false,
        filterable: false,
        groupable: false,
        hideable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: "center",
        headerAlign: "center",
        renderHeader: () => <div className={styles.header}>Met.</div>,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as IPIDeliverance;
          if (!row?.Id) return null;
          return (
            <Tooltip title="Metryka doręczenia" placement="left">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  cursor: "pointer",
                }}
                onClick={() => setMetricOf(row)}
              >
                <InfoIcon style={{ width: 18, height: 18 }} />
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [searchText],
  );

  useEffect(() => {
    setSubtitle("Wszystkie");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.list}>
        <DataGridPremium
          apiRef={apiRef}
          initialState={initialState}
          localeText={plGridLocales}
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
          slotProps={{
            loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
          }}
          loading={isLoading}
          columnHeaderHeight={30}
          columns={columns}
          rows={rows}
          hideFooter
          disableColumnFilter
          disablePivoting
          disableAggregation
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          columnVisibilityModel={columnVisibilityModel}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              { outline: "none !important" },
          }}
          onColumnVisibilityModelChange={(m) => {
            setColumnVisibilityModel(m);
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          onColumnOrderChange={() => {
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
        />
      </div>

      {metricOf && (
        <PIDeliveranceMetricSlide
          deliverance={metricOf}
          onClose={() => setMetricOf(null)}
        />
      )}
    </div>
  );
};

export default PIDeliverancesRegister;
